import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { getDeviceLocationSnapshot } from "@/lib/deviceLocation";
import { ConditionBadge } from "@/components/Badges";
import { CustodyShield } from "@/components/CustodyShield";
import { SyncBar } from "@/components/SyncBar";
import { HelpHint } from "@/components/HelpHint";
import { PhotoVoiceNote } from "@/components/PhotoVoiceNote";
import { MeterReadings } from "@/components/MeterReadings";
import { useSync } from "@/context/SyncContext";
import { checkImageQuality, qualityCopy } from "@/lib/qualityCheck";
import { PhotoLabelSuggestions } from "@/components/PhotoLabelSuggestions";
import { SameViewCaptureGuide } from "@/components/SameViewCaptureGuide";
import { PairingPendingPrompt } from "@/components/PairingPendingPrompt";
import { RoomWalkthroughVideo } from "@/components/RoomWalkthroughVideo";
import { UrgentConditionCapture } from "@/components/UrgentConditionCapture";
import { Button } from "@/components/ui/button";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InfoTip } from "@/components/InfoTip";
import {
  X, Camera, Check, Loader2, Sparkles, Plus, DoorOpen, ChevronDown, AlertTriangle, Trash2, Lightbulb, CloudOff, History, Upload, RotateCw, Layers, Siren,
} from "lucide-react";

const SHOT_SLOTS = [
  { key: "wide", label: "Wide shot", short: "Wide" },
  { key: "floors", label: "Floors & walls", short: "Floors" },
  { key: "closeup", label: "Close-up", short: "Close" },
];

export default function Capture() {
  const { propertyId, roomId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(roomId);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [roomSheet, setRoomSheet] = useState(false);
  const [newRoom, setNewRoom] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [showCoach, setShowCoach] = useState(() => !localStorage.getItem("dd_capture_coach"));
  const [locNoteShown, setLocNoteShown] = useState(false); // eslint-disable-line no-unused-vars
  const [reviewQueue, setReviewQueue] = useState([]); // instant quality check: files flagged, awaiting Keep/Retake
  const [armedSlot, setArmedSlot] = useState(null);   // shot-list: slot to tag the next capture with
  const [showGuide, setShowGuide] = useState(false);  // Feature 16: Same-View Capture Guide modal
  const [urgentOpen, setUrgentOpen] = useState(false); // Feature 29: Urgent Condition Capture
  const { captureAndSync, pendingByRoom, syncTick } = useSync();
  const offlinePhotos = pendingByRoom ? pendingByRoom(currentRoom) : [];

  const dismissCoach = () => {
    localStorage.setItem("dd_capture_coach", "1");
    setShowCoach(false);
  };

  const loadProperty = useCallback(async () => {
    const { data } = await api.get(`/properties/${propertyId}`);
    setProperty(data);
    setRooms(data.rooms);
  }, [propertyId]);

  const loadCoverage = useCallback(async (rid) => {
    try {
      const { data } = await api.get(`/rooms/${rid}/coverage`);
      setCoverage(data);
    } catch (e) { console.error("loadCoverage failed", e); }
  }, []);

  const loadPhotos = useCallback(async (rid) => {
    const { data } = await api.get(`/rooms/${rid}/photos`);
    setPhotos(data);
    loadCoverage(rid);
    return data;
  }, [loadCoverage]);

  useEffect(() => { loadProperty(); }, [loadProperty]);
  useEffect(() => { if (currentRoom) loadPhotos(currentRoom); }, [currentRoom, loadPhotos]);

  // Poll while any photo is being analyzed
  useEffect(() => {
    const pending = photos.some((p) => p.ai_status === "pending" || p.ai_status === "processing");
    if (!pending) return;
    const t = setInterval(() => loadPhotos(currentRoom), 2500);
    return () => clearInterval(t);
  }, [photos, currentRoom, loadPhotos]);

  const retryPhoto = async (photoId) => {
    try {
      await api.post(`/photos/${photoId}/retry-processing`, { retry_target: "photo_analysis" });
      setPhotos((ps) => ps.map((p) => (p.photo_id === photoId ? { ...p, ai_status: "processing" } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  // Reload server photos when an offline photo finishes syncing (offline chip → analysis chip).
  useEffect(() => { if (currentRoom) loadPhotos(currentRoom); }, [syncTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentRoomObj = rooms.find((r) => r.room_id === currentRoom);
  const retro = property?.import_mode === "retroactive";
  const flaggedCount = photos.filter((p) => p.ai_status === "done" && p.quality?.verdict === "retake").length;
  const retakePhoto = () => fileRef.current?.click();

  const queueRecord = (file, uploadId, capturedAt, dl, extra = {}) => ({
    client_upload_id: uploadId,
    property_id: propertyId,
    room_id: currentRoom,
    room_label: currentRoomObj?.name || "Room",
    phase: property?.doc_type || "move_in",
    image_blob: file, // EXACT accepted bytes — matches the server-side SHA-256 custody hash
    content_type: file.type || "image/jpeg",
    client_captured_at: capturedAt,
    import_mode: property?.import_mode || "live",
    quality_flags: extra.qualityFlags?.length
      ? JSON.stringify({ flags: extra.qualityFlags, kept_despite_warning: true })
      : (extra.qualityFlagsRaw || null),
    shot_slot: extra.shotSlot || null,
    // Feature 19: frozen, opt-in device-location snapshot (owner-only). Survives the offline queue
    // and idempotent retries; null when the setting is off or permission was denied.
    device_location: dl || null,
  });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const slot = armedSlot;
    setArmedSlot(null);
    const clean = [];
    const flagged = [];
    for (const file of files) {
      let flags = [];
      try { flags = await checkImageQuality(file); } catch { flags = []; } // degrade silently
      if (flags.length) {
        flagged.push({
          id: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
          file, flags, slot, url: URL.createObjectURL(file),
        });
      } else {
        clean.push(file);
      }
    }
    if (clean.length) uploadFiles(clean, { shotSlot: slot });
    if (flagged.length) setReviewQueue((q) => [...q, ...flagged]);
  };

  // Shared upload path (resilient queue): persist EXACT bytes to IndexedDB first, then attempt
  // the network upload. Offline / dropped connections leave the item queued for the queue worker.
  const uploadFiles = async (files, extra = {}) => {
    if (!files.length) return;
    setUploading((n) => n + files.length);
    if (navigator.vibrate) navigator.vibrate(30);
    const dl = await getDeviceLocationSnapshot();
    let queuedOffline = 0;
    let memoryOnlyFailed = 0;
    for (const file of files) {
      const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const capturedAt = new Date().toISOString();
      try {
        const res = await captureAndSync(queueRecord(file, uploadId, capturedAt, dl, extra));
        if (res.photo) await loadPhotos(currentRoom);
        else if (res.persisted) queuedOffline += 1;   // committed to this device, syncs later
        else memoryOnlyFailed += 1;                    // could not save locally AND could not upload
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (queuedOffline > 0) toast("Saved on this device — syncs when you\u2019re back online in TenantSeal");
    if (memoryOnlyFailed > 0) toast.error("Couldn\u2019t save this photo on your device or upload it. Keep this tab open and try again.");
  };

  // Same-View Capture Guide: upload a single camera-derived File through the EXACT same resilient
  // queue (client quality check + /rooms/{id}/photos), returning the created photo for pairing.
  const uploadCapturedFile = async (file) => {
    let flags = [];
    try { flags = await checkImageQuality(file); } catch { flags = []; }
    const dl = await getDeviceLocationSnapshot();
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const capturedAt = new Date().toISOString();
    const res = await captureAndSync(queueRecord(file, uploadId, capturedAt, dl, { qualityFlags: flags }));
    await loadPhotos(currentRoom);
    return res.photo; // null when queued offline — SameView surfaces its own "not paired" message
  };


  const keepReview = (item) => {
    uploadFiles([item.file], { qualityFlags: item.flags, shotSlot: item.slot });
    URL.revokeObjectURL(item.url);
    setReviewQueue((q) => q.filter((r) => r.id !== item.id));
  };

  const retakeReview = (item) => {
    URL.revokeObjectURL(item.url); // discard local file — nothing was uploaded
    setReviewQueue((q) => q.filter((r) => r.id !== item.id));
    setTimeout(() => fileRef.current?.click(), 60);
  };

  const assignSlot = async (photoId, slotKey) => {
    const cur = photos.find((p) => p.photo_id === photoId)?.shot_slot;
    const next = cur === slotKey ? null : slotKey;
    setPhotos((ps) => ps.map((p) => (p.photo_id === photoId ? { ...p, shot_slot: next } : p)));
    try { await api.patch(`/photos/${photoId}/shot-slot`, { shot_slot: next }); }
    catch { toast.error("Couldn't update the shot."); loadPhotos(currentRoom); }
  };

  const slotDone = (key) => photos.some((p) => p.shot_slot === key);

  const addRoom = async () => {
    if (!newRoom.trim()) return;
    const { data } = await api.post(`/properties/${propertyId}/rooms`, { name: newRoom.trim() });
    setNewRoom("");
    await loadProperty();
    setCurrentRoom(data.room_id);
    navigate(`/capture/${propertyId}/${data.room_id}`, { replace: true });
    setRoomSheet(false);
    toast.success(`Added ${data.name}`);
  };

  const switchRoom = (rid) => {
    setCurrentRoom(rid);
    navigate(`/capture/${propertyId}/${rid}`, { replace: true });
    setRoomSheet(false);
  };

  const deletePhoto = async (photoId) => {
    await api.delete(`/photos/${photoId}`);
    setPhotos((ps) => ps.filter((p) => p.photo_id !== photoId));
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D] text-white flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 h-16 border-b border-white/10 gap-2" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button data-testid="capture-close-btn" onClick={() => navigate(`/property/${propertyId}`)} className="grid place-items-center h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 transition-colors shrink-0">
          <X size={20} />
        </button>
        <button
          data-testid="room-switcher-btn"
          onClick={() => setRoomSheet(true)}
          className="flex items-center gap-2 px-4 h-11 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 transition-colors min-w-0 flex-1 max-w-[220px] justify-center"
        >
          <DoorOpen size={16} className="text-amber shrink-0" />
          <span className="font-medium truncate">{currentRoomObj?.name || "Room"}</span>
          <ChevronDown size={16} className="opacity-70 shrink-0" />
        </button>
        <HelpHint topic="capture" dark className="shrink-0" />
        <Button
          data-testid="capture-done-btn"
          onClick={() => navigate(`/property/${propertyId}`)}
          className="h-11 rounded-full bg-amber hover:bg-amber-hover active:scale-95 text-navy font-semibold px-5 shrink-0"
        >
          <Check size={16} className="mr-1" /> Done
        </Button>
      </header>

      {property?.address && (
        <div data-testid="capture-property-context" className="px-4 py-2 border-b border-white/5 bg-white/[0.03]">
          <p className="text-xs text-white/70 truncate"><span className="text-white/40">Documenting</span> · {property.address}</p>
        </div>
      )}

      <SyncBar dark />

      {propertyId && (
        <div className="mx-4 mt-3">
          <PairingPendingPrompt propertyId={propertyId} />
        </div>
      )}

      {/* Retroactive import banner */}
      {retro && (
        <div className="mx-4 mt-3 rounded-2xl border border-sky-400/40 bg-sky-400/10 p-3 flex items-start gap-2.5" data-testid="retroactive-banner">
          <History size={18} className="text-sky-300 mt-0.5 shrink-0" />
          <p className="text-sm text-sky-100 flex-1">Your photos' original dates are read from their metadata and shown on your report.</p>
        </div>
      )}

      {/* First-visit coach mark */}
      {showCoach && (
        <div data-testid="capture-coach-mark" className="mx-4 mt-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-3 flex items-start gap-2.5">
          <Lightbulb size={18} className="text-amber mt-0.5 shrink-0" />
          <p className="text-sm text-amber-100 flex-1">Take photos of everything — watch the Missing Shots list shrink as you go.</p>
          <button data-testid="dismiss-coach-btn" onClick={dismissCoach} aria-label="Dismiss tip" className="grid place-items-center h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 transition-colors shrink-0">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Missing coverage warning */}
      {coverage && coverage.missing?.length > 0 && photos.length > 0 && (
        <div className="mx-4 mt-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-3.5" data-testid="coverage-warning">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-200 inline-flex items-center gap-1">
                Missing shots in {currentRoomObj?.name}
                <InfoTip topic="missingCoverage" dark side="bottom" />
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {coverage.missing.map((m) => (
                  <span key={m.tag} data-testid={`missing-${m.tag}`} className="rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-100 text-xs px-2.5 py-1">
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {coverage && coverage.complete && (
        <div className="mx-4 mt-3 rounded-2xl border border-emerald-400/40 bg-emerald-400/10 p-3 flex items-center gap-2.5" data-testid="coverage-complete">
          <Check size={18} className="text-emerald-400" />
          <p className="text-sm font-semibold text-emerald-200">All recommended shots captured for {currentRoomObj?.name}</p>
        </div>
      )}

      {/* Photo quality coaching (non-blocking) */}
      {flaggedCount > 0 && (
        <div className="mx-4 mt-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-3 flex items-center gap-2.5" data-testid="quality-banner">
          <AlertTriangle size={18} className="text-amber-400 shrink-0" />
          <p className="text-sm font-semibold text-amber-200">
            {flaggedCount} photo{flaggedCount > 1 ? "s" : ""} could be stronger — retake for best evidence
          </p>
        </div>
      )}

      {/* Shot-list overlay — explicit, skippable, drives guided coverage (FREE) */}
      {!retro && currentRoom && (
        <div className="mx-4 mt-3" data-testid="shot-list-strip">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-white/40 shrink-0">Shot list</span>
            {SHOT_SLOTS.map((s, i) => {
              const done = slotDone(s.key);
              const armed = armedSlot === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`shot-chip-${s.key}`}
                  onClick={() => setArmedSlot(armed ? null : s.key)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    done ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-200"
                    : armed ? "border-amber bg-amber/20 text-amber"
                    : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                >
                  {done
                    ? <Check size={13} />
                    : <span className="grid place-items-center h-4 w-4 rounded-full border border-current text-[9px] font-bold">{i + 1}</span>}
                  {s.label}
                </button>
              );
            })}
            <button
              data-testid="urgent-capture-shortcut"
              onClick={() => setUrgentOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-amber/40 bg-amber/10 px-3 py-1.5 text-xs font-medium text-amber hover:bg-amber/20 transition-colors"
            >
              <Siren size={13} /> Urgent
            </button>
          </div>
          {armedSlot && (
            <p className="mt-1 text-[11px] text-amber-200/90" data-testid="shot-armed-hint">
              Next photo will be tagged “{SHOT_SLOTS.find((s) => s.key === armedSlot)?.label}”. Tap again to clear.
            </p>
          )}
        </div>
      )}

      {/* Photo grid */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {photos.length === 0 && offlinePhotos.length === 0 && uploading === 0 ? (
          <div className="h-full min-h-[50vh] flex flex-col items-center justify-center text-center">
            <span className="grid place-items-center h-20 w-20 rounded-3xl bg-white/5 border border-white/10">
              {retro ? <Upload size={34} className="text-white/60" /> : <Camera size={34} className="text-white/60" />}
            </span>
            <h2 className="mt-5 font-heading text-2xl font-semibold">{retro ? "Upload photos of" : "Capture"} {currentRoomObj?.name}</h2>
            <p className="mt-2 text-white/60 max-w-xs">{retro ? "Choose existing photos of this room from your gallery. Their original dates come from the file metadata." : "Snap every wall, floor, fixture and any existing damage. Tap the button below."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {Array.from({ length: uploading }).map((_, i) => (
              <div key={`up-${i}`} className="aspect-square rounded-2xl bg-white/5 border border-white/10 grid place-items-center">
                <Loader2 className="animate-spin text-white/50" />
              </div>
            ))}
            {offlinePhotos.map((op) => (
              <OfflineThumb key={op.client_upload_id} record={op} />
            ))}
            <AnimatePresence>
              {[...photos].reverse().map((p) => (
                <motion.div
                  key={p.photo_id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col"
                  data-testid={`capture-photo-${p.photo_id}`}
                >
                  <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 group">
                  <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                  <CustodyShield custody={p.custody} deviceLocation={p.device_location} className="absolute top-2 left-2 z-10" />
                  {(p.ai_status === "pending" || p.ai_status === "processing") && (
                    <div className="absolute inset-0 bg-black/30 overflow-hidden">
                      <div className="dd-scan-beam" />
                      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-[11px] font-medium bg-black/50 rounded-full px-2 py-1">
                        <Sparkles size={12} className="text-amber animate-pulse" /> Analyzing…
                      </div>
                    </div>
                  )}
                  {p.ai_status === "done" && (
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                      <ConditionBadge condition={p.ai_condition} testId={`cond-${p.photo_id}`} />
                      {p.ai_issues?.length > 0 && (
                        <span className="grid place-items-center h-6 w-6 rounded-full bg-[#FEF3C7] text-[#92400E]" title={`${p.ai_issues.length} issues`}>
                          <AlertTriangle size={13} />
                        </span>
                      )}
                    </div>
                  )}
                  {p.ai_status === "error" && (
                    <div data-testid={`photo-failed-${p.photo_id}`} className="absolute inset-0 bg-[#7F1D1D]/85 flex flex-col items-center justify-center gap-1 text-white text-center px-2">
                      <AlertTriangle size={16} />
                      <span className="text-[10px] font-bold leading-tight">Analysis failed</span>
                      <button data-testid={`retry-photo-${p.photo_id}`} onClick={() => retryPhoto(p.photo_id)} className="rounded-full bg-white text-[#7F1D1D] text-[10px] font-bold px-2.5 py-0.5 hover:bg-white/90">Tap to Retry</button>
                    </div>
                  )}
                  <button
                    data-testid={`delete-photo-${p.photo_id}`}
                    onClick={() => deletePhoto(p.photo_id)}
                    aria-label="Delete photo"
                    className="absolute top-2 right-2 grid place-items-center h-11 w-11 rounded-full bg-black/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 active:bg-[#B91C1C] hover:bg-[#B91C1C] transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                  </div>
                  {p.ai_status === "done" && p.quality && (
                    p.quality.verdict === "retake" ? (
                      <div data-testid={`quality-retake-${p.photo_id}`} className="mt-1.5 rounded-xl border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-200">
                            <AlertTriangle size={12} /> Retake suggested
                          </span>
                          <button data-testid={`retake-photo-${p.photo_id}`} onClick={retakePhoto}
                            className="flex items-center gap-1 rounded-full bg-amber-400 text-[#3a2a00] text-[10px] font-bold px-2 py-0.5 hover:bg-amber-300 active:scale-95 transition">
                            <Camera size={11} /> Retake
                          </button>
                        </div>
                        {p.quality.reasons?.length > 0 && (
                          <p className="mt-0.5 text-[10px] leading-tight text-amber-100/90">{p.quality.reasons.join(" · ")}</p>
                        )}
                      </div>
                    ) : (
                      <div data-testid={`quality-good-${p.photo_id}`} className="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium text-emerald-300">
                        <Check size={12} /> Good evidence photo
                      </div>
                    )
                  )}
                  <PhotoVoiceNote photoId={p.photo_id} initialTranscript={p.voice_note_transcript} />
                  {!retro && (
                    <div className="mt-1 flex items-center gap-1" data-testid={`shot-assign-${p.photo_id}`}>
                      {SHOT_SLOTS.map((s) => (
                        <button
                          key={s.key}
                          data-testid={`assign-${p.photo_id}-${s.key}`}
                          onClick={() => assignSlot(p.photo_id, s.key)}
                          className={`flex-1 rounded-md px-1 py-1 text-[9px] font-semibold border transition-colors ${
                            p.shot_slot === s.key
                              ? "border-amber bg-amber/20 text-amber"
                              : "border-white/10 bg-white/5 text-white/45 hover:bg-white/10"
                          }`}
                        >
                          {s.short}
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        {property && photos.length > 0 && (
          <div className="max-w-3xl mx-auto mt-5" data-testid="capture-label-suggestions">
            <PhotoLabelSuggestions propertyId={propertyId} photos={photos} dark
              readOnly={!!property?.is_locked}
              onChanged={() => loadPhotos(currentRoom)} />
          </div>
        )}
        {property && (
          <div className="max-w-3xl mx-auto mt-5">
            <MeterReadings propertyId={propertyId} phase={property.doc_type} />
          </div>
        )}
        {property && currentRoom && (
          <RoomWalkthroughVideo
            propertyId={propertyId}
            roomId={currentRoom}
            locked={!!property?.is_locked}
            archived={property?.archive_status === "archived" || property?.archive_status === "surrendered"}
          />
        )}
      </div>

      {/* Capture button (thumb zone) */}
      <div className="px-4 pt-3 pb-6 border-t border-white/10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.25rem)" }}>
        {property?.doc_type === "move_out" && property?.linked_property_id && !retro && !property?.is_locked && currentRoom && (
          <div className="max-w-3xl mx-auto mb-3 flex justify-center">
            <button data-testid="same-view-guide-btn" onClick={() => setShowGuide(true)}
              className="inline-flex items-center gap-2 rounded-full border border-amber/40 bg-amber/10 text-amber text-sm font-medium px-4 py-2 active:scale-95 transition-transform">
              <Layers size={16} /> Use Same-View Guide
            </button>
          </div>
        )}
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-6">
          <span className="text-sm text-white/50 w-24 text-right hidden sm:block">{photos.length} photos</span>
          {retro ? (
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="capture-file-input" />
          ) : (
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onFiles} className="hidden" data-testid="capture-file-input" />
          )}
          <button
            data-testid="capture-photo-trigger"
            onClick={() => fileRef.current?.click()}
            className="relative grid place-items-center h-[74px] w-[74px] rounded-full bg-white active:scale-95 transition-transform ring-4 ring-white/20"
          >
            <span className="grid place-items-center h-16 w-16 rounded-full bg-brand text-white">
              {retro ? <Upload size={28} /> : <Camera size={28} />}
            </span>
          </button>
          <span className="w-24 hidden sm:block" />
        </div>
      </div>

      {/* Instant quality-check review (advises; never blocks — Keep always available) */}
      {reviewQueue.length > 0 && (() => {
        const item = reviewQueue[0];
        return (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="quality-review-modal">
            <div className="w-full max-w-sm rounded-2xl bg-[#16233A] border border-white/10 p-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}>
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={20} className="text-amber shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-semibold text-white" data-testid="quality-review-copy">{qualityCopy(item.flags)}</p>
                  <p className="text-xs text-white/60 mt-0.5">Quick quality check — you decide. Keep it if it’s the shot you want.</p>
                </div>
              </div>
              <img src={item.url} alt="preview" data-testid="quality-review-preview"
                className="mt-3 w-full max-h-[42vh] object-contain rounded-xl border border-white/10 bg-black/30" />
              <div className="mt-4 grid grid-cols-2 gap-2.5">
                <Button data-testid="quality-retake-btn" onClick={() => retakeReview(item)}
                  className="h-12 rounded-xl bg-white/10 hover:bg-white/20 text-white">
                  <RotateCw size={16} className="mr-1.5" /> Retake
                </Button>
                <Button data-testid="quality-keep-btn" onClick={() => keepReview(item)}
                  className="h-12 rounded-xl bg-amber hover:bg-amber-hover text-navy font-semibold">
                  <Check size={16} className="mr-1.5" /> Keep
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Room switcher sheet */}
      <Drawer open={roomSheet} onOpenChange={setRoomSheet}>
        <DrawerContent className="bg-white text-[#0F172A]">
          <DrawerHeader>
            <DrawerTitle className="font-heading text-xl">Rooms</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 space-y-2 max-h-[45vh] overflow-y-auto">
            {rooms.map((r) => (
              <button
                key={r.room_id}
                data-testid={`switch-room-${r.room_id}`}
                onClick={() => switchRoom(r.room_id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                  r.room_id === currentRoom ? "border-amber bg-amber-soft" : "border-[#E2E8F0] hover:bg-[#FAF7F2]"
                }`}
              >
                <span className="flex items-center gap-2.5 font-medium">
                  <DoorOpen size={18} className="text-brand" /> {r.name}
                </span>
                <span className="text-sm text-[#94A3B8]">{r.photo_count} photos</span>
              </button>
            ))}
          </div>
          <DrawerFooter>
            <div className="flex gap-2">
              <Input
                data-testid="new-room-input"
                value={newRoom}
                onChange={(e) => setNewRoom(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRoom()}
                placeholder="Add a room…"
                className="h-12 rounded-xl"
              />
              <Button data-testid="add-room-btn" onClick={addRoom} className="h-12 rounded-xl bg-navy hover:bg-navy-hover text-white px-4">
                <Plus size={18} />
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Feature 16 — Same-View Capture Guide (move-out only) */}
      {showGuide && currentRoom && (
        <SameViewCaptureGuide
          propertyId={propertyId}
          roomId={currentRoom}
          roomName={currentRoomObj?.name || "Room"}
          onUpload={uploadCapturedFile}
          onClose={() => { setShowGuide(false); loadPhotos(currentRoom); }}
        />
      )}

      <UrgentConditionCapture
        property={property}
        rooms={rooms}
        initialRoomId={currentRoom}
        open={urgentOpen}
        onOpenChange={setUrgentOpen}
        onLinked={() => loadPhotos(currentRoom)}
      />

    </div>
  );
}

function OfflineThumb({ record }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!record.image_blob) return undefined;
    const u = URL.createObjectURL(record.image_blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [record.image_blob]);

  const failed = record.status === "failed";
  const syncing = record.status === "syncing";
  return (
    <div
      data-testid={`offline-photo-${record.client_upload_id}`}
      className="relative aspect-square rounded-2xl overflow-hidden border border-amber-400/40"
    >
      {url && <img src={url} alt="" className="w-full h-full object-cover opacity-80" />}
      <div className="absolute inset-0 bg-black/20" />
      <div
        data-testid={failed ? "offline-chip-failed" : "offline-chip"}
        className={`absolute bottom-2 left-2 right-2 flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-1 ${
          failed ? "bg-[#7F1D1D] text-white" : "bg-amber-400/90 text-[#3b2a05]"
        }`}
      >
        {failed ? (
          <><AlertTriangle size={12} /> {record.last_error ? "Needs attention" : "Failed"}</>
        ) : syncing ? (
          <><Loader2 size={12} className="animate-spin" /> Syncing…</>
        ) : (
          <><CloudOff size={12} /> Saved offline — will sync</>
        )}
      </div>
    </div>
  );
}
